import { computed, DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { ActivatedRouteSnapshot, Route, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { API, CompanyDTO, UserClientFullDTO } from './api/api';
import { APIService } from './api/api.service';
import { ErrorService } from './error.service';
import { PermissionService } from './permission.service';
import { WebSettingsService } from './client/web-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserSettingService } from './user-setting.service';
import { UserClientSettingService } from './user-client-setting.service';
import { ClientSettingService } from './client-setting.service';
import { ViewSetting } from './global/global-interfaces';
import { KiService } from '../components/ki/ki.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly kiService = inject(KiService);

  selectedLanguage = this.userSettingService.selectedLanguage;
  userGeoLocation: GeolocationPosition = null;
  currentUserSettings: Signal<Record<string, string> | null> = this.userSettingService.userSettings.asReadonly();
  currentUserClientSettings: Signal<Record<string, string> | null> = this.userClientSettingService.userClientSettings.asReadonly();
  currentClientSettings: Signal<Record<string, string> | null> = this.clientSettingService.clientSettings.asReadonly();
  debugMode: boolean = this.userSettingService.debugMode();
  private _user: UserClientFullDTO;
  selectedCompany: BehaviorSubject<CompanyDTO> = new BehaviorSubject<CompanyDTO>(null);
  defaultViewSetting = computed<ViewSetting>(() => {
    const currentUserSettings = this.currentUserSettings();
    // get default viewSetting or assume it is 'list'
    return currentUserSettings?.defaultView && (currentUserSettings.defaultView === 'list' || currentUserSettings.defaultView === 'card')
      ? currentUserSettings.defaultView
      : 'list';
  });

  viewSettingObject = computed<Record<string, ViewSetting> | null>(() => {
    const currentUserSettings = this.currentUserSettings();
    const defaultView = this.defaultViewSetting();
    // derive all viewSettings from UserSettings
    let viewSettings: Record<string, ViewSetting> = {};
    if (currentUserSettings) {
      viewSettings = Object.fromEntries(
        // create record from Array
        Object.entries(currentUserSettings)
          .filter(([key]) => key.includes('ViewSetting')) // filter out ViewSetting from UserSetting
          .map(([key, value]) => {
            // map filtered Value to match (key: string, value: ViewSetting)
            return [key, value && (value === 'list' || value === 'card') ? value : 'list'];
          })
      );
    }
    // add default view to derived viewSettings
    viewSettings.defaultView = defaultView;
    return viewSettings;
  });

  public get user(): UserClientFullDTO {
    return this._user;
  }

  public set user(value: UserClientFullDTO) {
    this._user = value;
    if (this._user) {
      this.permissionService.userPermissions = this._user.effectivePermissions;
      this.getGeoLocation();
      // load new Settings from server
      this.initializeSettingServices().then(() => this.kiService.initializeKIConnection());
    }
  }

  constructor(
    private readonly api: API,
    private readonly apiService: APIService,
    private readonly errorHandler: ErrorService,
    private readonly webSettings: WebSettingsService,
    private readonly userSettingService: UserSettingService,
    private readonly userClientSettingService: UserClientSettingService,
    private readonly clientSettingService: ClientSettingService,
    private readonly permissionService: PermissionService,
    private readonly destroyRef: DestroyRef
  ) {}

  private async initializeSettingServices() {
    await this.userSettingService.getUserSettingsFromServer();
    await this.userClientSettingService.getUserClientSettingsFromServer();
    await this.clientSettingService.getClientSettingsFromServer();
    // load web settings from server
    await this.webSettings.refreshFromServer();
  }

  getGeoLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position: GeolocationPosition) => {
        this.userGeoLocation = position;
      });
    }
  }

  getSharedStatus(route: ActivatedRouteSnapshot | Route, state?: RouterStateSnapshot) {
    if (state?.url?.startsWith('/admin') || this.user) {
      return true;
    }
    // no user, then do status call to check if jwt valid if valid set user otherwise delete jwt and redirect to login page
    this.getGeoLocation();
    return this.api.shared_Status_status().pipe(
      map(data => {
        this.user = data;
        return true;
      }),
      take(1),
      takeUntilDestroyed(this.destroyRef),
      catchError(error => {
        if (error) {
          if (error.status === 401) {
            this.apiService.logout();
          }
        }
        this.userSettingService.userSettings.set(null);
        return of(false);
      })
    );
  }

  /* userSettings methods */

  setUserSetting(id: string, value: string): Promise<void> {
    return this.userSettingService.setUserSetting(id, value);
  }

  clearUserSetting(id: string): Promise<void> {
    return this.userSettingService.clearUserSetting(id);
  }

  async getAllUserSettings(): Promise<Record<string, string>> {
    return await this.userSettingService.getAllUserSettings();
  }

  clearAllUserSettings(): Promise<void> {
    return this.userSettingService.clearAllUserSettings();
  }

  /* userClientSettings methods */

  getUserClientSetting(id: string): Promise<string | null> {
    return this.userClientSettingService.getUserClientSetting(id);
  }

  setUserClientSetting(id: string, value: string): Promise<void> {
    return this.userClientSettingService.setUserClientSetting(id, value);
  }

  clearUserClientSetting(id: string): Promise<void> {
    return this.userClientSettingService.clearUserClientSetting(id);
  }

  getAllUserClientSettings(): Promise<Record<string, string>> {
    return this.userClientSettingService.getAllUserClientSettings();
  }

  clearAllUserClientSettings(): Promise<void> {
    return this.userClientSettingService.clearAllUserClientSettings();
  }

  /* ClientSettings methods */

  getClientSetting(id: string) {
    return this.clientSettingService.getClientSetting(id);
  }

  setClientSetting(id: string, value: any): Promise<void> {
    return this.clientSettingService.setClientSetting(id, value);
  }

  clearClientSetting(id: string): Promise<void> {
    return this.clientSettingService.clearClientSetting(id);
  }

  getAllClientSettings(): Promise<Record<string, string>> {
    return this.clientSettingService.getAllClientSettings();
  }

  clearAllClientSettings(): Promise<void> {
    return this.clientSettingService.clearAllClientSettings();
  }

  retrieveUser() {
    this.api
      .shared_Status_status()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (data: UserClientFullDTO) => {
          this.user = data;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }
}
